<script>
import { DpIconCaret } from '@dp-ui-kit/icons';
import { h, nextTick } from 'vue';

import { PLACEMENT_BOTTOM, PLACEMENT_LEFT } from '../../config/constants';
import { getSlotContent } from '../../utils';
import {
  DROPDOWN_ACTIVE_ITEM_DATA_ATTRIBUTE,
  DROPDOWN_CODE_DOWN,
  DROPDOWN_CODE_ENTER,
  DROPDOWN_CODE_SPACE,
  DROPDOWN_MENU_ALIGNMENT,
  DROPDOWN_MENU_POSITIONS,
} from './config';
import DpDropdownHeader from './DpDropdownHeader.vue';
import DpDropdownItem from './DpDropdownItem.vue';
import dropdownMixin from './mixins/dropdown';

const hasWindow = typeof window !== 'undefined';
const hasNavigator = typeof navigator !== 'undefined';
const isTouch = hasWindow && ('ontouchstart' in window || (hasNavigator && navigator.msMaxTouchPoints > 0));
const events = isTouch ? ['touchstart', 'click'] : ['click'];

export default {
  name: 'DpDropdown',

  compatConfig: { MODE: 3 },

  components: {
    DpIconCaret,
    DpDropdownHeader,
    DpDropdownItem,
  },

  mixins: [dropdownMixin],

  provide() {
    return {
      onSelect: this.onSelect,
    };
  },

  props: {
    navigateWithKeys: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * @values HTML tag name (div, ul)
     * @default div
     * */
    tag: {
      type: String,
      required: false,
      default: 'div',
    },

    /**
     * @values HTML tag name or component name, (span, a, button, div)
     * @default a
     * */
    toggleTag: {
      type: String,
      required: false,
      default: 'a',
    },

    /**
     * @values 'btn btn-primary btn-light btn-default btn-lg' | ['btn', 'btn-primary', 'btn-light', 'btn-default', 'btn-lg'] | { btn: true, 'btn-primary': true, 'btn-light': true, 'btn-default': true, 'btn-lg': true }
     * */
    toggleClass: {
      type: [String, Array, Object],
      required: false,
      default: null,
    },

    /**
     * @values See at 'DpDropdownItem' props
     * */
    items: {
      type: Array,
      required: false,
      default: () => [],
    },

    /**
     * @values bottom, top
     * @default bottom
     * */
    menuPosition: {
      type: String,
      required: false,
      default: PLACEMENT_BOTTOM,
      validator: (position) => DROPDOWN_MENU_POSITIONS.includes(position),
    },

    /**
     * @values left, right
     * @default left
     * */
    menuAlignment: {
      type: String,
      required: false,
      default: PLACEMENT_LEFT,
      validator: (position) => DROPDOWN_MENU_ALIGNMENT.includes(position),
    },

    scrollable: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Use it to open/close the dropdown.
     * */
    open: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: [
    'blur',
    'open',
    'close',
    'select',
  ],

  data: () => ({
    isOpen: false,
  }),

  watch: {
    open: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        this.isOpen = newVal;

        if (newVal) {
          this.onOpen();

          return;
        }

        if (typeof oldVal === 'undefined') {
          return;
        }

        this.onClose();
      },
    },
  },

  unmounted() {
    this.tearDownListeners();
  },

  methods: {
    setUpListeners() {
      events.forEach((evt) => document.addEventListener(evt, this.clickOutside));
    },

    tearDownListeners() {
      events.forEach((evt) => document.removeEventListener(evt, this.clickOutside));
    },

    clickOutside(event) {
      const { target } = event;
      const { dropdownMenu, dropdownToggle } = this.$refs;
      const clickedInsideToggle = dropdownToggle.contains(target);
      const clickedOutsideMenu = !dropdownMenu.contains(target);

      if (clickedInsideToggle) {
        return;
      }

      if (clickedOutsideMenu) {
        /**
         * Emitted on blurring the dropdown
         *
         * @type {boolean}
         */
        this.$emit('blur');
        this.onClose();
      }
    },

    toggle(isOpen = null, byKeyboard = null) {
      this.isOpen = isOpen !== null ? isOpen : !this.isOpen;

      if (this.isOpen) {
        this.onOpen();

        if (byKeyboard || !this.scrollable) {
          return;
        }

        nextTick(() => {
          const { dropdownMenuContent } = this.$refs;
          const activeElement = dropdownMenuContent.querySelector(`[${DROPDOWN_ACTIVE_ITEM_DATA_ATTRIBUTE}="true"]`);

          if (activeElement instanceof HTMLElement) {
            dropdownMenuContent.scrollTo({
              top: activeElement.offsetTop,
              left: 0,
              behavior: 'smooth',
            });
          }
        });
      } else {
        this.onClose();
      }
    },

    onOpen() {
      /**
       * Emitted on opening the dropdown
       *
       * @type {boolean}
       */
      this.$emit('open');
      this.setUpListeners();
    },

    onClose() {
      /**
       * Emitted on closing the dropdown
       *
       * @type {boolean}
       */
      this.$emit('close');
      this.isOpen = false;
      this.tearDownListeners();
    },

    onSelect(event, params) {
      /**
       * Emitted after dropdown item is selected
       *
       * @property {object} event MouseEvent's object
       * @property {object} params item properties
       */
      this.$emit('select', event, params);
      this.toggle();
    },

    dropdownToggleContent() {
      const toggleSlot = getSlotContent(this.$slots.toggle);

      if (toggleSlot) {
        return toggleSlot;
      }

      return h(
        DpIconCaret,
        {
          class: ['m-0'],
          size: 12,
        },
      );
    },

    dropdownToggle() {
      const vm = this;

      return h(
        this.toggleTag,
        {
          ref: 'dropdownToggle',
          class: [
            this.toggleClass,
            'dropdown-toggle',
            {
              disabled: this.toggleTag !== 'button' && this.disabled,
            }],
          href: this.toggleTag === 'a' ? '#' : null,
          'aria-haspopup': 'true',
          'aria-expanded': this.isOpen ? 'true' : 'false',
          disabled: this.toggleTag === 'button' && this.disabled,
          onClick(e) {
            e.preventDefault();

            if (!vm.disabled) {
              vm.toggle();
            }
          },
          onKeydown(e) {
            if (e.key === 'Escape' && vm.isOpen) {
              vm.toggle(!vm.isOpen, true);
            }

            if (!vm.navigateWithKeys) {
              return;
            }

            const { type, keyCode } = e;

            if (
              type !== 'click' && !(type === 'keydown' && [DROPDOWN_CODE_ENTER, DROPDOWN_CODE_SPACE, DROPDOWN_CODE_DOWN].indexOf(keyCode) !== -1)) {
              return;
            }

            e.preventDefault();

            if (!vm.disabled) {
              vm.toggle(null, true);
            }
          },
        },
        [
          this.dropdownToggleContent(),
        ],
      );
    },

    dropdownMenuFooter() {
      if (!this.$slots.footer) {
        return null;
      }

      return h(
        'div',
        {
          class: ['dropdown-menu-footer', 'pt-1-5', 'pb-1', 'px-1-5', 'd-flex', 'border-top', 'justify-content-between'],
        },
        this.$slots.footer({
          close: this.onClose,
        }),
      );
    },

    dropdownMenuContent() {
      const defaultSlot = this.$slots.default;
      const itemsLength = this.items.length;

      if (!defaultSlot && itemsLength === 0) {
        return null;
      }

      // Handle default slot content
      if (itemsLength === 0) {
        return defaultSlot({
          select: this.onSelect,
        });
      }

      // Handle `items` prop
      return this.items.map((props) => {
        if (props.type === 'header') {
          return h(DpDropdownHeader, props);
        }

        if (props.type === 'separator') {
          return h('hr');
        }

        return h(DpDropdownItem, props);
      });
    },

    dropdownMenuContentWrapper() {
      return h(
        'ul',
        {
          ref: 'dropdownMenuContent',
          class: {
            'dropdown-menu-no-list-styles': true,
            'dropdown-menu-scrollable': this.scrollable,
          },
        },
        [
          this.dropdownMenuContent(),
        ],
      );
    },

    dropdownMenu() {
      return h(
        'div',
        {
          ref: 'dropdownMenu',
          class: {
            'dropdown-menu': true,
            'dropdown-menu-top': this.menuPosition === 'top',
            'dropdown-menu-right': this.menuAlignment === 'right',
            show: this.isOpen,
          },
          tabindex: this.navigateWithKeys ? '-1' : null,
          onKeydown: this.onKeydown,
        },
        [
          this.dropdownMenuContentWrapper(),
          this.dropdownMenuFooter(),
        ],
      );
    },
  },

  /**
   * @slot default
   * @binding select Bind to select event
   */
  /**
   * @slot footer
   * @binding close Bind to close event
   */
  /**
   * @slot toggle
   */
  render() {
    return h(
      this.tag,
      {
        class: {
          'dp-dropdown': true,
          dropdown: this.menuPosition === 'bottom',
          dropup: this.menuPosition === 'top',
          show: this.isOpen,
        },
      },
      [
        this.dropdownToggle(),
        this.dropdownMenu(),
      ],
    );
  },
};
</script>

<style lang="scss">
@use "../../../scss/src/bootstrap/variables" as *;

.dropdown-menu .dropdown-item:focus {
  background-color: $gray-300;
}
</style>
